import { ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import axios from 'axios';
import { IAction } from '../../../helpers/interfaces';
import { getLoginRequestHeader } from '../../../utils';
import { apiError } from '../../actions/user-actions';
import { SERVER_URL } from '../../../utils/config';
import {
    ApiAccessTypes,
    createApiAccessUserSuccess,
    getApiAccessRolesSuccess,
    getApiAccessUserEndpointsSuccess,
    getApiAccessUserSuccess,
    getApiAccessUsersSuccess,
    updateApiAccessPasswordSuccess,
    updateApiAccessUserSuccess,
} from '../../actions/configProvider/api-access-actions';

export const getApiAccessRolesEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(ApiAccessTypes.API_ACCESS_ROLES_REQUEST),
        mergeMap(() => {
            const headers = getLoginRequestHeader();

            return from(
                axios.get(`${SERVER_URL}/public-api/roles`, {
                    headers,
                }),
            ).pipe(
                map(({ data }) => getApiAccessRolesSuccess(data)),
                catchError((error) =>
                    of(apiError(error, ApiAccessTypes.API_ACCESS_ROLES_REQUEST)),
                ),
            );
        }),
    );

export const getApiAccessUsersEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(ApiAccessTypes.API_ACCESS_USERS_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader();
            const { username, principalType, blocked, role, paging } = action.payload || {};
            const params: Record<string, any> = {
                username,
                principalType,
                blocked,
                role,
            };

            if (paging) {
                params['paging.pageNumber'] = paging.page;
                params['paging.pageSize'] = paging.pageSize;
                params['paging.sortBy'] = paging.sortBy;
            }

            return from(
                axios.get(`${SERVER_URL}/public-api/users`, {
                    headers,
                    params,
                }),
            ).pipe(
                map(({ data }) => getApiAccessUsersSuccess(data)),
                catchError((error) =>
                    of(apiError(error, ApiAccessTypes.API_ACCESS_USERS_REQUEST)),
                ),
            );
        }),
    );

export const getApiAccessUserEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(ApiAccessTypes.API_ACCESS_USER_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader();
            return from(axios.get(`${SERVER_URL}/public-api/users/${action.payload}`, { headers }))
                .pipe(
                    map(({ data }) => getApiAccessUserSuccess(data)),
                    catchError((error) =>
                        of(apiError(error, ApiAccessTypes.API_ACCESS_USER_REQUEST)),
                    ),
                );
        }),
    );

export const createApiAccessUserEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(ApiAccessTypes.API_ACCESS_CREATE_USER_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader('application/json');

            return from(
                axios.post(`${SERVER_URL}/public-api/users`, action.payload || {}, { headers }),
            ).pipe(
                map(({ data }) => createApiAccessUserSuccess(data)),
                catchError((error) =>
                    of(apiError(error, ApiAccessTypes.API_ACCESS_CREATE_USER_REQUEST)),
                ),
            );
        }),
    );

export const updateApiAccessUserEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(ApiAccessTypes.API_ACCESS_UPDATE_USER_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader('application/json');
            const { id, ...payload } = action.payload || {};

            return from(
                axios.put(`${SERVER_URL}/public-api/users/${id}`, payload, { headers }),
            ).pipe(
                map(({ data }) => updateApiAccessUserSuccess(data)),
                catchError((error) =>
                    of(apiError(error, ApiAccessTypes.API_ACCESS_UPDATE_USER_REQUEST)),
                ),
            );
        }),
    );

export const updateApiAccessPasswordEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(ApiAccessTypes.API_ACCESS_UPDATE_PASSWORD_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader('application/json');
            const { id, ...payload } = action.payload || {};

            return from(
                axios.put(`${SERVER_URL}/public-api/users/${id}/password`, payload, { headers }),
            ).pipe(
                map(({ data }) => updateApiAccessPasswordSuccess(data)),
                catchError((error) =>
                    of(apiError(error, ApiAccessTypes.API_ACCESS_UPDATE_PASSWORD_REQUEST)),
                ),
            );
        }),
    );

export const getApiAccessUserEndpointsEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(ApiAccessTypes.API_ACCESS_USER_ENDPOINTS_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader();
            return from(
                axios.get(`${SERVER_URL}/public-api/users/${action.payload}/endpoints`, {
                    headers,
                }),
            ).pipe(
                map(({ data }) => getApiAccessUserEndpointsSuccess(data)),
                catchError((error) =>
                    of(apiError(error, ApiAccessTypes.API_ACCESS_USER_ENDPOINTS_REQUEST)),
                ),
            );
        }),
    );
